package dev.lingvo.shared.screenshots.design.derive

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.FitSpec
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.scene.Placement
import kotlin.math.round

/** Group children stay in parent coordinates; semantic placements apply only at the page root. */
internal fun resizePage(
    page: Page, sx: Double, sy: Double, frame: FrameKind, ids: Map<String, String>,
    placements: Map<Pair<Role, Int>, Placement>,
    scaleFits: Boolean = false,
): Page {
    fun box(value: Box) = value.copy(x = number(value.x * sx), y = number(value.y * sy),
        w = maxOf(1.0, number(value.w * sx)), h = maxOf(1.0, number(value.h * sy)))
    fun fit(value: FitSpec): FitSpec = if (!scaleFits) value else value.copy(
        maxSize = number(value.maxSize * minOf(sx, sy)), floorSize = number(value.floorSize * minOf(sx, sy)))
    fun layers(items: List<Layer>, root: Boolean): List<Layer> = items.map { layer ->
        val placement = if (root) placements[layer.base.role to layer.base.roleSlot]?.box else null
        val bounds = placement ?: box(layer.base.box)
        val base = layer.base.copy(id = ids.getValue(layer.base.id), box = bounds)
        fun radius(value: Double) = value.coerceAtMost(minOf(bounds.w, bounds.h) / 2)
        when (layer) {
            is BackgroundLayer -> layer.copy(base = base)
            is DeviceLayer -> layer.copy(base = base, frame = frame)
            is TextLayer -> layer.copy(base = base)
            is ImageLayer -> layer.copy(base = base, cornerRadius = radius(layer.cornerRadius))
            is ShapeLayer -> layer.copy(base = base, cornerRadius = radius(layer.cornerRadius),
                points = layer.points.map { it.copy(x = number(it.x * sx), y = number(it.y * sy)) })
            is GroupLayer -> layer.copy(base = base, children = layers(layer.children, false), cornerRadius = radius(layer.cornerRadius))
        }
    }
    return page.copy(layers = layers(page.layers, true), overrides = page.overrides.mapValues { (_, override) ->
        override.copy(patches = override.patches.map { (id, patch) ->
            ids.getValue(id) to patch.copy(box = patch.box?.let(::box), fit = patch.fit?.let(::fit))
        }.toMap())
    })
}

/** Deterministic traversal is also the identity-allocation order. */
internal fun flattenResizeLayers(items: List<Layer>): List<Layer> = items.flatMap {
    listOf(it) + if (it is GroupLayer) flattenResizeLayers(it.children) else emptyList()
}

private fun number(value: Double): Double = (round(value * 1_000_000.0) / 1_000_000.0).let { if (it == 0.0) 0.0 else it }
